<script setup lang="ts">
/**
 * A title's crest: the carved frame for its rank, with its arms inside.
 *
 * The frame art is one image per rank and the arms are painted into a canvas
 * behind it, positioned to sit in the frame's opening. County frames are
 * solid by design, so a county shows the frame alone.
 */
import { computed, onMounted, ref, watch } from 'vue';

import { TITLE_CRESTS } from '@/assets/art/art';
import { paintSymbol } from '@/heraldry/paint';
import { realArmsFor } from '@/heraldry/realArms';
import { symbolFor } from '@/heraldry/symbols';

const props = withDefaults(
  defineProps<{
    tier: 'county' | 'duchy' | 'kingdom' | 'empire';
    name: string;
    kingdomId?: string | null;
    empireId?: string | null;
    /** Width of the whole crest, frame included. */
    size?: number;
  }>(),
  { size: 64, kingdomId: null, empireId: null },
);

const canvas = ref<HTMLCanvasElement | null>(null);

const frame = computed(() => TITLE_CRESTS[props.tier]);
/*
 * The real coat where one exists, and a plain device otherwise.
 *
 * Never an invented shield. A title that bore no arms gets one mark on the
 * bare opening instead, which claims nothing about who carried what.
 */
const real = computed(() =>
  realArmsFor(props.tier, props.name, props.kingdomId, props.empireId),
);
const symbol = computed(() =>
  real.value || props.tier === 'county' ? null : symbolFor(props.name, props.kingdomId),
);

/**
 * Where the opening sits inside each frame, and how big it is.
 *
 * Measured off the plates rather than guessed at: each frame was flood-filled
 * inward from the middle to find the actual hole, and these are its centre and
 * size as fractions of the artwork. The four frames are drawn at quite
 * different weights, so guessing was never going to work.
 *
 * `fill` overshoots the hole slightly on purpose. The arms are drawn *behind*
 * the frame, so anything that spills is hidden under the frame's inner lip,
 * and a shield that slightly overfills its opening looks set into it. One that
 * politely fits inside looks like a sticker.
 */
const OPENING: Record<string, { x: number; y: number; w: number; h: number }> = {
  county: { x: 0.5, y: 0.5, w: 0, h: 0 },
  duchy: { x: 0.499, y: 0.56, w: 0.487, h: 0.528 },
  kingdom: { x: 0.501, y: 0.53, w: 0.489, h: 0.475 },
  empire: { x: 0.501, y: 0.563, w: 0.436, h: 0.497 },
};

const FILL = 1.1;

const shieldBox = computed(() => {
  const o = OPENING[props.tier]!;
  const frameHeight = props.size * (frame.value.h / frame.value.w);
  return {
    w: Math.round(props.size * o.w * FILL),
    h: Math.round(frameHeight * o.h * FILL),
    cx: o.x * props.size,
    cy: o.y * frameHeight,
  };
});

const shieldStyle = computed(() => ({
  left: `${shieldBox.value.cx - shieldBox.value.w / 2}px`,
  top: `${shieldBox.value.cy - shieldBox.value.h / 2}px`,
}));

function repaint(): void {
  const sym = symbol.value;
  if (!canvas.value || !sym) return;
  paintSymbol(canvas.value, sym.kind, sym.tincture, shieldBox.value.w, shieldBox.value.h);
}

onMounted(repaint);
watch([symbol, shieldBox], repaint, { flush: 'post' });
</script>

<template>
  <span class="crest" :style="{ width: `${size}px` }" :title="name">
    <img
      v-if="real"
      class="crest__arms crest__arms--real"
      :src="real.src"
      :alt="`arms of ${name}`"
      :style="{ ...shieldStyle, width: `${shieldBox.w}px`, height: `${shieldBox.h}px` }"
    />
    <canvas v-else-if="symbol" ref="canvas" class="crest__arms" :style="shieldStyle" />
    <img class="crest__frame" :src="frame.src" :alt="`${tier} crest`" :width="size" />
  </span>
</template>

<style scoped lang="scss">
.crest {
  position: relative;
  display: inline-block;
  line-height: 0;
  flex: none;
}

.crest__arms--real {
  /*
   * Filled, not fitted.
   *
   * `contain` kept every coat whole and left most of them floating in a ring
   * of empty frame, because a shield is taller than the opening is and the
   * openings are not all the same shape. `cover` scales until the hole is
   * full and lets the overflow be clipped, which costs a sliver off the top
   * or the sides of some coats and makes the crest read as one carved object
   * instead of a picture sitting inside a border.
   */
  object-fit: cover;
  object-position: center;
}

.crest__arms {
  position: absolute;
  // Behind the frame, so the frame's inner lip overlaps the shield's edge and
  // the two read as one carved object rather than a sticker on a picture.
  z-index: 0;
}

.crest__frame {
  position: relative;
  z-index: 1;
  height: auto;
  display: block;
  filter: drop-shadow(0 2px 4px rgb(0 0 0 / 45%));
}
</style>
